// src/Pages/CustomerLoginPage.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../../redux/actions/authActions";
import { useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";

const CustomerLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState(1); // 1 = mobile input, 2 = OTP input
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);

  const { loading, error } = useSelector((state) => state.auth);

  // Handle mobile number input validation
  const handleMobileChange = (e) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/\D/g, ""); // remove non-digit characters

    // If input is empty or starts with 6-9 and <= 10 digits
    if (
      cleanedValue === "" ||
      (/^[6-9]/.test(cleanedValue) && cleanedValue.length <= 10)
    ) {
      setMobile(cleanedValue);
    }
  };

  // Handle OTP input validation
  const handleOtpChange = (e) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/\D/g, ""); // remove non-digit characters

    if (cleanedValue.length <= 4) {
      setOtp(cleanedValue);
    }
  };

  // Move to OTP step
  const handleContinue = (e) => {
    e.preventDefault();

    if (mobile.length !== 10) {
      alert("Please enter a valid 10-digit mobile number");
      return;
    }

    setStep(2);
  };

  // Login with mobile and OTP using Redux action
  const handleLogin = async (e) => {
    e.preventDefault();

    if (otp.length !== 4) {
      alert("Please enter a valid 4-digit OTP");
      return;
    }

    try {
      // Dispatch loginUser with both mobile and OTP
      await dispatch(loginUser({ mobile, otp }));

      // If successful, get user info from localStorage (already saved by Redux action)
      const user = JSON.parse(localStorage.getItem("userInfo"));
      const token = user?.access_token;

      if (token) {
        // Decode JWT to get user_type
        const base64Url = token?.split(".")[1];
        const base64 = base64Url?.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = base64 ? JSON.parse(atob(base64)) : null;
        const userType = jsonPayload?.user_type;

        // Navigate based on user type
        if (userType === "Customer") {
          navigate("/dashboard");
        } else {
          navigate("/dashboard");
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      // Error already handled by Redux action with toast
    }
  };

  // Go back to mobile input
  const handleChangeNumber = () => {
    setStep(1);
    setOtp("");
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100">
      <div className="bg-white border border-amber-500 rounded-lg p-8 shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold text-center mb-6 text-amber-600">
          Customer Login
        </h2>

        {step === 1 ? (
          // Step 1: Mobile Number Input
          <div>
            <div className="mb-6 relative">
              <input
                type="text"
                name="mobile"
                value={mobile}
                onChange={handleMobileChange}
                className="peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500"
                placeholder=" "
                required
              />
              <label
                className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500
                  peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                  peer-focus:-top-2 peer-focus:text-xs peer-focus:text-amber-500"
              >
                Mobile Number
              </label>
            </div>

            <button
              onClick={handleContinue}
              className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={mobile.length !== 10}
            >
              Continue
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              Enter your registered mobile number to continue
            </p>
          </div>
        ) : (
          // Step 2: OTP Input and Login
          <div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 text-center mb-4">
                Enter OTP for <span className="font-semibold">{mobile}</span>
                <button
                  onClick={handleChangeNumber}
                  className="text-amber-600 hover:underline ml-2 text-xs"
                >
                  Change
                </button>
              </p>
            </div>

            <div className="mb-6 relative">
              <input
                type={showOtp ? "text" : "password"}
                name="otp"
                value={otp}
                onChange={handleOtpChange}
                className="peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500 pr-10 tracking-widest text-center text-lg"
                placeholder=" "
                maxLength="4"
                required
              />
              <label
                className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500
                  peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                  peer-focus:-top-2 peer-focus:text-xs peer-focus:text-amber-500"
              >
                Enter OTP
              </label>

              {/* Eye icon for toggling OTP visibility */}
              <div
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500"
                onClick={() => setShowOtp(!showOtp)}
              >
                {showOtp ? (
                  <FiEyeOff size={20} className="text-amber-600" />
                ) : (
                  <FiEye size={20} className="text-amber-600" />
                )}
              </div>
            </div>

            <button
              onClick={handleLogin}
              className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={loading || otp.length !== 4}
            >
              {loading ? "Logging in..." : "Login"}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              For testing, use OTP: <span className="font-semibold">0000</span>
            </p>
          </div>
        )}

        {error && (
          <p className="text-red-500 text-sm text-center mt-4">{error}</p>
        )}
      </div>
    </div>
  );
};

export default CustomerLogin;
